<template>
  <div class="documents-checkout" data-cy="documents-checkout">
    <div class="header-container">
      <h3 class="page-title">
        {{ $t('documentsCheckout.title') }}
      </h3>
      <router-link to="/orders">
        <template v-slot="{ navigate }">
          <button @click="navigate" class="btn-orders">
            <backstage-button icon-right="arrow-right">
              {{ $t('viewAccounting.order_button') }}
            </backstage-button>
          </button>
        </template>
      </router-link>
    </div>
    <div v-if="loading" class="loader"></div>
    <div v-else class="main-container">
      <div v-if="documents.length" class="section-container documents-container">
        <div class="document-item header">
          <span class="document-number" data-cy="document-number">
            {{ $t('documentsCheckout.document_number') }}
          </span>
          <span class="amount">
            {{ $t('documentsCheckout.document_amount') }}
          </span>
        </div>
        <div class="documents-list">
          <div v-for="(document, index) in documents" :key="document.documentNumber" class="document-item">
            <span class="document-number" data-cy="document-number-value">
              {{ document.documentNumber }}
              <button class="btn-download" data-cy="download-button" v-on:click="openDocument(document, index)">
                <backstage-icon v-if="document.loading" class="icon isLoading" name="loading" />
                <backstage-icon v-else class="icon" name="download" />
              </button>
            </span>
            <span class="amount">{{ formatCurrency(document.currencyCode, document.outstandingValue) }}</span>
          </div>
        </div>
        <div class="document-item totals-overview" data-cy="totals-overview">
          <span class="document-number">{{ $t('documentsCheckout.total') }}</span>
          <span class="amount">{{ formatCurrency(documents[0].currencyCode, documentsTotal) }}</span>
        </div>
      </div>
      <div class="section-container payment-form">
        <div v-if="availablePaymentMethods" data-cy="payment-methods-grid">
          <h4 class="sub-title">
            {{ $t('documentsCheckout.select_payment_method') }}
          </h4>
          <div
            class="radio-options-container"
            :class="{ disabled: paymentMethod.disabled || checkoutLoading }"
            v-for="paymentMethod in availablePaymentMethods"
            :key="paymentMethod.name">
            <label
              class="radio-options"
              :data-cy="'payment-method-' + paymentMethod.type"
              :for="paymentMethod.type"
              v-on:click="selectPaymentMethod(paymentMethod)">
              <input :id="paymentMethod.type" :key="paymentMethod.type" type="radio" name="paymentMethodType" />
              <p>{{ $t(`documentsCheckout[\'${paymentMethod.type.toLocaleLowerCase()}\']`) }}</p>
            </label>
            <p v-if="paymentMethod.showPaymentMethodMsg" class="payment-msg">{{ paymentMethod.paymentMethodMsg }}</p>
          </div>
          <BillingAddressForm
            v-if="showAchBillingForm"
            v-model="achBillingAddress"
            :selected-account="selectedAccount" />
          <div id="adyen-payment-container" data-hj-suppress></div>

          <label v-if="!!selectedCardBrandIsInCorrect" class="error">{{
            $t('documentsCheckout.credit_card_not_supported')
          }}</label>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import type { BillingAddress } from '@/components/common/BillingAddressForm.vue';
import BillingAddressForm from '@/components/common/BillingAddressForm.vue';
import { useBPLogger } from '@/composables/business-process-logger';
import { useDocumentHelpers } from '@/composables/document-helpers';
import { useLocale } from '@/composables/locale';
import { AppSettings } from '@/constants/app.settings';
import { COUNTRIES_WITH_PAYMENT_OPTION } from '@/mixins/account.mixins';
import { ordersMixins } from '@/mixins/orders.mixins';
import { paymentsMixins } from '@/mixins/payments.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { AccountingDocument } from '@/models/accounting-document.model';
import { type PaymentMethod, PaymentMethodType, UnknownCreditCardBrand } from '@/models/payments.model';
import { AccountingDocumentsService } from '@/services/accounting-documents.service';
import { OrderDocumentService } from '@/services/order-document-service';
import { PaymentsService } from '@/services/payments.service';
import { useAccountBalanceStore } from '@/stores/account-balance';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import { Ach, AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import { PaymentStatus, ValidatorResult } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent, reactive } from 'vue';
import { useFormat } from '../composables/format';

interface PaymentMethodWithMsg extends PaymentMethod {
  disabled: boolean;
  paymentMethodMsg: string;
  showPaymentMethodMsg: boolean;
}

export default defineComponent({
  name: 'documents-checkout',
  components: {
    'backstage-button': BackstageButton,
    'backstage-icon': BackstageIcon,
    BillingAddressForm,
  },
  props: {
    documentReferenceIds: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());
    const { userLocale } = useLocale();
    const userStore = useUserStore();
    const { openPDF } = useDocumentHelpers();
    const { formatCurrency } = useFormat();
    const { pageViewBPLog } = useBPLogger();
    const { accountBalance, isCreditCardPaymentEnabled, isCreditCardPaymentExceeded, creditCardAnnualCap } =
      storeToRefs(useAccountBalanceStore());
    const { getAccountBalance } = useAccountBalanceStore();
    const { setPaymentData, setPaymentId } = usePaymentStore();
    const { paymentData, paymentId } = storeToRefs(usePaymentStore());

    return {
      accountId: selectedAccountId,
      userLocale,
      selectedAccount,
      userStore,
      openPDF,
      formatCurrency,
      pageViewBPLog,
      accountBalance,
      getAccountBalance,
      isCreditCardPaymentEnabled,
      isCreditCardPaymentExceeded,
      creditCardAnnualCap,
      paymentData,
      setPaymentData,
      paymentId,
      setPaymentId,
    };
  },
  computed: {
    documentReferenceIdsArray(): string[] {
      return this.documentReferenceIds?.split(',');
    },
    availablePaymentMethods(): PaymentMethodWithMsg[] {
      const methods = this.filterAvailablePaymentMethods(this.paymentMethods)
        .map(this.attachPaymentMethodMsg)
        // move credit card methods to the top of the list
        .sort((a) => (a.type === PaymentMethodType.Card ? -1 : 1));

      return methods;
    },
  },
  watch: {
    accountId(newVal) {
      if (newVal) {
        this.getAccountDocuments();
        this.getAccountBalance(newVal);
      }
    },
    achBillingAddress: {
      handler() {
        this.updatePayButtonState();
      },
      deep: true,
    },
  },
  data() {
    return {
      loading: true,
      documents: [] as AccountingDocument[],
      documentsTotal: 0,
      paymentsService: new PaymentsService(),
      accountingDocumentsService: new AccountingDocumentsService(),
      orderDocumentsService: new OrderDocumentService(),
      paymentMethods: [] as PaymentMethod[],
      selectedPaymentMethod: undefined as PaymentMethod | undefined,
      adyenClientKey: import.meta.env.VITE_APP_adyenClientKey,
      paymentMethodDetails: undefined,
      adyenCheckout: undefined as any | undefined,
      selectedCardBrandIsInCorrect: undefined as boolean | undefined,
      cardFieldStyles: {
        color: '#000000',
        fontFamily: '"On", Helvetica, sans-serif',
        fontSize: '0.875rem',
        fontWeight: '400',
      },
      defaultCountryCodeForAdyen: 'US',
      checkoutLoading: true,
      adyenComponent: undefined as any | undefined,
      showAchBillingForm: false,
      achBillingAddress: {
        street: '',
        houseNumber: '',
        city: '',
        postalCode: '',
        stateOrProvince: '',
        country: '',
      } as BillingAddress,
      adyenComponentIsValid: false,
    };
  },
  async mounted() {
    const [result, documentsResult] = await Promise.all([
      this.paymentsService.getPaymentMethods(this.accountId as string),
      this.accountingDocumentsService.getAll(this.accountId as string), // ToDo we should only get the required documents
      this.getAccountBalance(this.accountId as string),
    ]);

    this.documents =
      documentsResult.model?.filter(
        (document) =>
          (this.documentReferenceIdsArray.includes(document.referenceId.toString()) &&
            parseFloat(document.outstandingValue as any)) ||
          0 !== 0,
      ) || [];

    this.documentsTotal = this.calculateTotalAmount(this.documents);
    this.loading = false;

    if (result.model) {
      const adyenEnv = AppSettings.isProduction ? ('live-us' as const) : ('test' as const);

      const adyenCheckoutConfiguration = {
        showPayButton: true,
        paymentMethodsResponse: result.model,
        clientKey: this.adyenClientKey,
        locale: this.mapToAdyenLocale(this.userLocale),
        environment: adyenEnv,
        countryCode: this.getCountryCodeForAdyen(),
        onChange: (state: any) => {
          this.adyenComponentIsValid = state.isValid;

          if (this.adyenComponentIsValid) {
            this.paymentMethodDetails = state.data;
          }

          this.updatePayButtonState();
        },
        onSubmit: async (state: any, component: any) => {
          component.setStatus('loading');
          if (this.selectedCardBrandIsInCorrect) {
            component.setStatus('ready');

            return;
          }

          let paymentAddress = state.data.billingAddress; // From Card

          if (this.selectedPaymentMethod?.type !== PaymentMethodType.Card) {
            paymentAddress = this.achBillingAddress; // From ACH
          }

          const response = await this.paymentsService.checkout(this.accountId as string, {
            invoices: this.documents.map((document) => ({
              referenceId: document.referenceId,
              documentNumber: document.documentNumber,
              voucher: document.voucher,
              amount: document.outstandingValue,
            })),
            paymentMethod: state.data.paymentMethod,
            paymentAddress: state.data.billingAddress || paymentAddress,
            amount: this.documentsTotal,
            shopperEmail: this.selectedAccount.b2bAccountManagerEmail,
            browserInfo: state.data.browserInfo,
            channel: 'Web',
            origin: globalThis.location.origin,
            authenticationData: {
              threeDSRequestData: {
                nativeThreeDS: 'preferred',
              },
            },
          });

          console.log('Browser info:', state.data.browserInfo);

          this.setPaymentData(response?.model?.model?.action?.paymentData);
          this.setPaymentId(response?.model?.model?._id);

          if (response.model?.errors?.length > 0) {
            this.showSubmissionMessages(response, component);

            return;
          }

          if (response.model?.model?.action) {
            component.handleAction(response.model?.model?.action);
          } else {
            this.showSubmissionMessages(response, component);
          }
        },
        // Adyen’s risk engine and the issuing bank will decide whether to trigger 3DS payment flow via native SDK component
        onAdditionalDetails: async (state: any, component: any) => {
          const response = await this.paymentsService.checkoutDetails(this.accountId as string, {
            ...state.data.details,
            paymentId: this.paymentId,
            paymentData: this.paymentData,
          });

          if (response?.model?.model?.resultCode === PaymentStatus.Authorised) {
            this.showSubmissionMessages(response, component);
          } else {
            this.$snotify.error(
              this.$t(response?.model?.errors[0]?.error) || this.$t('documentsCheckout.threeds_payment_failed'),
            );
          }
        },
        onPaymentCompleted: async (result: any, component: any) => {
          component.setStatus('success');
        },
        onPaymentFailed: async (result: any, component: any) => {
          component.setStatus('error');
        },
      };

      this.paymentMethods = result.model.paymentMethods;
      this.adyenCheckout = await AdyenCheckout(adyenCheckoutConfiguration);
      this.checkoutLoading = false;
    }

    console.log(this.pageViewBPLog(true, { page_name: 'documents-checkout' }));
  },
  beforeUnmount() {
    if (this.adyenComponent) {
      this.adyenComponent.unmount();
    }
  },
  methods: {
    ...utilsMixins,
    ...ordersMixins,
    ...paymentsMixins,
    isAchBillingAddressValid(): boolean {
      const addr = this.achBillingAddress;

      return !!(addr.street && addr.city && addr.postalCode && addr.country);
    },
    updatePayButtonState() {
      this.$nextTick(() => {
        const payButton = document.querySelector(
          '#adyen-payment-container .adyen-checkout__button--pay',
        ) as HTMLButtonElement;

        if (payButton) {
          let isCustomFormValid = true;

          if (this.showAchBillingForm) {
            isCustomFormValid = this.isAchBillingAddressValid();
          }
          const isOverallValid = this.adyenComponentIsValid && isCustomFormValid;
          payButton.disabled = !isOverallValid;
        }
      });
    },
    attachPaymentMethodMsg(method: PaymentMethod): PaymentMethodWithMsg {
      let showPaymentMethodMsg = false;
      let paymentMethodMsg = this.$t(`documentsCheckout.payment_method.${method.type}`);
      if (this.isCreditCardPaymentEnabled && this.creditCardAnnualCap > 0) {
        showPaymentMethodMsg = true;
        if (method.type === PaymentMethodType.Card) {
          if (this.isCreditCardPaymentExceeded(this.documentsTotal)) {
            paymentMethodMsg = this.$t(`documentsCheckout.payment_method.${method.type}`, {
              limit: this.formatCurrency(this.accountBalance?.currencyCode, this.creditCardAnnualCap),
            });
          } else {
            showPaymentMethodMsg = false;
          }
        }
      }

      return {
        ...method,
        disabled: method.type === PaymentMethodType.Card && this.isCreditCardPaymentExceeded(this.documentsTotal),
        paymentMethodMsg,
        showPaymentMethodMsg,
      };
    },
    filterAvailablePaymentMethods(paymentMethods: PaymentMethod[]) {
      if (!this.isCreditCardPaymentEnabled) {
        return paymentMethods?.filter((method) => {
          return method.type !== PaymentMethodType.Card;
        });
      }

      return paymentMethods;
    },
    getCountryCodeForAdyen(): string {
      COUNTRIES_WITH_PAYMENT_OPTION.find((country) => {
        if (country.code === this.selectedAccount?.billingCountry.slice(0, -1)) {
          return country.code;
        }
      });

      return this.defaultCountryCodeForAdyen;
    },
    selectPaymentMethod(paymentMethod: PaymentMethod) {
      this.selectedPaymentMethod = paymentMethod;

      if (this.adyenComponent) {
        this.adyenComponent.unmount();
      }

      this.showAchBillingForm = paymentMethod.type !== PaymentMethodType.Card;

      if (paymentMethod.type === PaymentMethodType.Ach) {
        this.selectedCardBrandIsInCorrect = undefined;
        const achConfig = {
          placeholders: {
            ownerName: 'J. Smith',
            accountNumber: '123456789',
            routingNumber: '021000021',
          },
          style: 'material',
          i18n: this.adyenCheckout.modules.i18n,
          enableStoreDetails: false,
        };
        this.adyenComponent = new Ach(this.adyenCheckout, achConfig).mount('#adyen-payment-container');
      } else {
        const cardConfig: any = {
          placeholders: {
            holderName: 'J Smith',
            expiryDate: 'MM/YY',
            cardNumber: '1234 5678 9012 3456',
            encryptedSecurityCode: '123',
          },
          style: 'material',
          hasHolderName: true,
          holderNameRequired: true,
          i18n: this.adyenCheckout.modules.i18n,
          maskSecurityCode: true,
          enableStoreDetails: false,
          billingAddressRequired: true,
          billingAddressAllowedCountries: COUNTRIES_WITH_PAYMENT_OPTION.map(({ code }) => code),
          countryCode: this.getCountryCodeForAdyen(),
          onBrand: (data: any) => {
            if (data.brand === UnknownCreditCardBrand) {
              this.selectedCardBrandIsInCorrect = true;
            } else {
              this.selectedCardBrandIsInCorrect = false;
            }
          },
        };
        this.adyenComponent = new Card(this.adyenCheckout, cardConfig).mount('#adyen-payment-container');
      }

      this.updatePayButtonState();
    },
    async openDocument(document: AccountingDocument, index: number) {
      document.loading = true;
      this.documents = reactive(this.documents);
      this.documents[index] = document;

      const validation = await this.orderDocumentsService.get(
        document.salesOrderNumber as string,
        document.documentNumber,
        this.accountId as string,
        this.getDocumentTypeByDocumentNumberForPaymentDocuments(document.documentNumber),
      );

      if (validation.isValid() && validation.model?.base64Body) {
        this.openPDF(validation.model.base64Body, document.documentNumber, document.salesOrderNumber);
      } else {
        this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));
      }

      document.loading = false;
      this.documents = reactive(this.documents);
      this.documents[index] = document;
    },
    showSubmissionMessages(response: ValidatorResult<any>, dropin: any) {
      if (response.errors?.length > 0 || response.model.errors?.length > 0) {
        this.$snotify.error(this.$t(response.model.errors[0].error));
        dropin.setStatus('ready');
      } else {
        dropin.setStatus('success');
        this.$snotify.success(this.$t('documentsCheckout.submit_payment_success'));
        this.$router.push('/accounting');
      }
    },
    cancel() {
      this.getAccountDocuments();
    },
    getAccountDocuments() {
      this.$router.push('/accounting');
    },
  },
});
</script>

<style scoped lang="scss">
@mixin flex-row-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@mixin section-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--primary-text-color);
  border: none;
  padding: 0;
  margin: 0 0 1rem;
  text-align: left;
}

.documents-checkout {
  @include main-max-with;
  margin: 0 auto;

  .header-container {
    @include flex-row-container;
    box-sizing: border-box;
    padding: 1.5rem 1.25rem;
    height: 7.625rem;

    .page-title {
      font-size: 1.25rem;
      font-weight: 400;
      text-align: left;
    }

    .btn-orders {
      border: none;
      background-color: transparent;
      padding: 0;
    }
  }

  .main-container {
    @include flex-row-container;
    align-items: flex-start;
    flex-wrap: wrap;
    max-width: 70.625rem;
    padding: 0 1.25rem;
    margin: 0 auto 4rem;

    .sub-title {
      @include section-title;
    }

    .section-container {
      width: 49%;
      background: var(--primary-bg-color);
      padding: 1.25rem;
      border-radius: 4px;
      height: fit-content;
    }

    .documents-container {
      .document-item {
        @include flex-row-container;
        height: 3rem;
        padding: 0 1rem;

        &:nth-child(odd) {
          background-color: var(--bg-page-color);
        }

        &.header {
          background-color: var(--primary-bg-color);
          height: fit-content;
          padding: 0;

          .document-number,
          .amount {
            @include section-title;
          }
        }

        .document-number,
        .amount {
          @include flex-row-container;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(primary-text-color);

          .btn-download {
            height: 1.875rem;
            width: 1.875rem;
            cursor: pointer;
            border: none;
            background-color: inherit;

            .icon {
              width: 1.25rem;
              height: 1.25rem;

              &.isLoading {
                width: 30px;
                height: 30px;
                cursor: initial;
              }
            }
          }
        }

        &.totals-overview {
          margin-top: 1rem;
          background-color: var(--bg-remark-color);
          height: 3.5rem;

          .document-number,
          .amount {
            font-size: 1rem;
            font-weight: 700;
          }
        }
      }
    }

    .payment-form {
      .radio-options-container.disabled {
        opacity: 0.5;
        pointer-events: none;
      }
      .radio-options {
        @include flex-row-container;
        justify-content: flex-start;
        gap: 0.75rem;
        cursor: pointer;
        height: fit-content;
        margin-bottom: 0.75rem;

        input[type='radio'] {
          cursor: pointer;
          width: 1.375rem;
          height: 1.375rem;
          accent-color: var(--primary-text-color);
          margin: 0;
          padding: 0;
        }

        p {
          font-size: 0.875rem;
          font-weight: 400;
          text-align: center;
          margin: 0;
        }
      }

      .payment-msg {
        text-align: left;
        font-size: 0.75rem;
      }
      label.error {
        color: var(--scarlet-red-color);
        font-size: 0.875rem;
      }
    }
  }

  // we use deep to correctly override adyen styles
  #adyen-payment-container:deep() {
    margin-top: 1.8125rem;
    margin-bottom: 0.3125rem;

    @import '../assets/scss/overrides/adyen.scss';
  }

  @include breakpoint(small) {
    div.main-container {
      margin-bottom: 2rem;
      padding: 0 0.5rem;
      div.section-container {
        width: 100%;
        padding: 0.9375rem;
        margin-bottom: 0.5rem;
      }
    }
  }
}
</style>
